import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/device-management/navigate-back.svg';
import { AvailableTabProperties } from '../../../../interface/device-management.interface';
import TabsContainer from '../../../../shared-components/tabs-container/TabsContainer';

interface DeviceManagementTabsProperties {
  containerTabs: AvailableTabProperties[];
  handleSwitchTab: (key: string) => void;
  deviceId?: string;
  children?: React.ReactNode;
}

/**
 * Component to display the device management tabs
 * @param {DeviceManagementTabsProperties} properties - The properties passed to the component.
 * @returns {React.ReactElement} The rendered device management tabs component.
 */
function DeviceManagementTabs(properties: DeviceManagementTabsProperties) {
  const { i18n } = useTranslation('devices');
  const navigate = useNavigate();
  const { containerTabs, handleSwitchTab, children } = properties;
  const location = useLocation();
  let basePath = 'charger';
  if (
    location.pathname.includes('charger-expanded') ||
    location.pathname.includes('device-expanded')
  ) {
    basePath = location.pathname.includes('charger-expanded')
      ? 'charger-expanded'
      : 'device-expanded';
  } else if (
    location.pathname.includes('charger-management') ||
    location.pathname.includes('device-management')
  ) {
    basePath = location.pathname.includes('charger-management')
      ? 'charger-management'
      : 'device-management';
  } else if (location.pathname.includes('/device/')) {
    basePath = 'device';
  }
  /**
   * Navigates back to the previous or source page.
   * @returns {void}
   */
  const handleBack = (): void => {
    const querySource = new URLSearchParams(location.search).get('source');
    const sourcePath = location.state?.source || querySource;
    const currentFullPath = `${location.pathname}${location.search}`;

    if (sourcePath && sourcePath !== location.pathname && sourcePath !== currentFullPath) {
      navigate(sourcePath);
      return;
    }

    let updated = location.pathname.replace(new RegExp(`/${basePath}/[^/]+/[^/]+$`), '');
    if (updated === '/ev-admin' || updated === '/ev-admin/') {
      updated = '/ev-admin/operations/chargers';
    }

    if (updated !== location.pathname) {
      navigate(updated);
      return;
    }

    const hasPreviousHistory = Boolean(
      window.history.state && typeof window.history.state.idx === 'number'
        ? window.history.state.idx > 0
        : window.history.length > 1,
    );

    if (hasPreviousHistory) {
      navigate(-1);
    } else {
      navigate('/ev-admin/operations/chargers');
    }
  };

  return (
    <div className="container-tabs-wrap" dir={i18n.dir()}>
      <button type="button" className="navigate-arrow" onClick={handleBack}>
        <span>
          <NavigateArrow />
        </span>
        <div className="navigate-background" />
      </button>
      <TabsContainer containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />
      {children}
    </div>
  );
}

export default DeviceManagementTabs;
